import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { formatNlDate, formatEuro } from "@/components/Badges";
import { MoneyInput } from "@/components/MoneyInput";
import { parseAmount } from "@/lib/money";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { InlineError } from "@/components/ListState";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);

// Owner-only: the rate per hour type with its effective date, plus the
// repairs → werkbonnen migration button. An empty amount is NORMAL here — the
// owner is waiting on his accountant, so "tarief nog niet ingesteld" is a
// state, never € 0,00.
export const SettingsTarieven = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ uurtype: "normaal", tarief: "", ingangsdatum: today(), onderbouwing: "" });
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migratie, setMigratie] = useState(null);
  // §6b.2 correction dialog
  const [corDlg, setCorDlg] = useState(null); // regel object being corrected
  const [corForm, setCorForm] = useState({ tarief: "", onderbouwing: "", reden: "" });
  const [corSaving, setCorSaving] = useState(false);

  const load = async () => {
    setError("");
    try { const { data } = await api.get("/settings/tarieven"); setData(data); }
    catch (e) { setError(getErrorMessage(e, "Kan tarieven niet laden.")); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const { value, error: amtErr } = parseAmount(form.tarief);
    if (amtErr) { toast.error(amtErr); return; }
    if (!form.ingangsdatum) { toast.error("Kies een ingangsdatum."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/settings/tarieven/regels", {
        uurtype: form.uurtype, tarief_ex_btw: value, ingangsdatum: form.ingangsdatum,
        onderbouwing: form.onderbouwing.trim() || null,
      });
      setData(data);
      toast.success(value == null ? "Tariefregel zonder bedrag vastgelegd" : `Tarief vastgelegd: ${formatEuro(value)}`);
      setForm({ uurtype: "normaal", tarief: "", ingangsdatum: today(), onderbouwing: "" });
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSaving(false); }
  };

  const openCorrigeren = (r) => {
    setCorDlg(r);
    setCorForm({
      tarief: r.tarief_ex_btw == null ? "" : String(r.tarief_ex_btw).replace(".", ","),
      onderbouwing: r.onderbouwing || "",
      reden: "",
    });
  };

  const saveCorrigeren = async () => {
    const { value, error: amtErr } = parseAmount(corForm.tarief);
    if (amtErr) { toast.error(amtErr); return; }
    if (!corForm.reden.trim()) { toast.error("Geef een reden voor de correctie."); return; }
    setCorSaving(true);
    try {
      const { data } = await api.post(`/settings/tarieven/regels/${corDlg.id}/corrigeer`, {
        tarief_ex_btw: value,
        onderbouwing: corForm.onderbouwing.trim() || null,
        reden: corForm.reden.trim(),
      });
      setData(data);
      toast.success("Tariefregel gecorrigeerd — vorige bedrag vastgelegd in de historie.");
      setCorDlg(null);
    } catch (e) { toast.error(getErrorMessage(e, "Corrigeren mislukt.")); }
    finally { setCorSaving(false); }
  };

  const migrate = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post("/werkbonnen/migratie-reparaties");
      setMigratie(data);
      toast.success(`Migratie klaar: ${data.counts.gemigreerd} gemigreerd, ${data.counts.overgeslagen} stonden er al`);
    } catch (e) { toast.error(getErrorMessage(e, "Migratie mislukt.")); }
    finally { setMigrating(false); }
  };

  if (error) return <InlineError testid="tarieven-load-error" message={error} onRetry={load} />;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h2 className="mb-1 text-body font-semibold text-foreground">Uurtarieven (ex btw)</h2>
        <p className="mb-4 text-data text-muted-foreground">
          Groepsbreed, per uurtype, met een ingangsdatum. Een <strong>nieuw tarief per datum</strong> herrekent nooit
          oudere bonnen. Een <strong>correctie</strong> herstelt een verkeerd ingevoerd bedrag zonder de datum aan
          te raken en wordt in de historie vastgelegd.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(data?.uurtypes || []).map((u) => {
            const actueel = data?.actueel?.[u.key];
            return (
              <div key={u.key} className="border border-lijn-zacht p-4" data-testid={`tarief-actueel-${u.key}`}>
                <div className="text-label uppercase tracking-[0.07em] text-muted-foreground">{u.label}</div>
                <div className="mt-1 font-mono tabular-nums text-kop text-foreground">
                  {actueel != null ? formatEuro(actueel) : <span className="font-sans text-body text-muted-foreground">tarief nog niet ingesteld</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 border-t border-lijn-zacht pt-4">
          <h3 className="mb-2 text-body font-semibold text-foreground">Nieuw tarief per datum</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Uurtype</Label>
              <Select value={form.uurtype} onValueChange={(v) => setForm((f) => ({ ...f, uurtype: v }))}>
                <SelectTrigger data-testid="tarief-uurtype-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(data?.uurtypes || []).map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tarief (mag leeg)</Label>
              <MoneyInput testid="tarief-bedrag-input" value={form.tarief} onChange={(v) => setForm((f) => ({ ...f, tarief: v }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Ingangsdatum</Label>
              <Input type="date" data-testid="tarief-ingangsdatum-input" value={form.ingangsdatum} onChange={(e) => setForm((f) => ({ ...f, ingangsdatum: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Onderbouwing <span className="text-muted-foreground">(waarom is dit tarief wat het is)</span></Label>
              <Textarea
                data-testid="tarief-onderbouwing-input"
                value={form.onderbouwing}
                onChange={(e) => setForm((f) => ({ ...f, onderbouwing: e.target.value }))}
                placeholder="bijv. vergeleken met externe leverancier op deze prijs"
                rows={2}
              />
            </div>
          </div>
          <div className="mt-3">
            <Button data-testid="tarief-opslaan-button" onClick={save} disabled={saving}>{saving ? "Bezig..." : "Tarief vastleggen"}</Button>
          </div>
        </div>

        {(data?.regels || []).length > 0 && (
          <div className="mt-5 border-t border-lijn-zacht pt-3" data-testid="tarief-historie">
            <h3 className="mb-2 text-body font-semibold text-foreground">Historie</h3>
            <ol className="space-y-2">
              {data.regels.map((r) => {
                const label = (data.uurtypes.find((u) => u.key === r.uurtype) || {}).label || r.uurtype;
                return (
                  <li key={r.id || `${r.uurtype}-${r.ingangsdatum}-${r.created_at}`} className="border border-lijn-zacht p-3" data-testid={`tarief-regel-${r.id || r.uurtype}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-body text-foreground">
                          <span className="font-medium">{label}</span>:{" "}
                          <span className="font-mono tabular-nums">{r.tarief_ex_btw != null ? formatEuro(r.tarief_ex_btw) : "geen bedrag"}</span>{" "}
                          per {formatNlDate(r.ingangsdatum)}
                        </div>
                        {r.onderbouwing && (
                          <div className="mt-1 text-data text-inkt-zacht" data-testid={`tarief-onderbouwing-${r.id || r.uurtype}`}>
                            Onderbouwing: {r.onderbouwing}
                          </div>
                        )}
                        <div className="mt-1 text-data text-muted-foreground">
                          {r.created_by_name ? `Vastgelegd door ${r.created_by_name}` : ""}
                          {r.updated_by_name ? ` · laatst gecorrigeerd door ${r.updated_by_name}` : ""}
                        </div>
                        {(r.corrections || []).length > 0 && (
                          <ol className="mt-2 space-y-0.5 border-t border-lijn-zacht pt-2 text-data text-muted-foreground" data-testid={`tarief-corrections-${r.id || r.uurtype}`}>
                            {r.corrections.map((c, i) => (
                              <li key={i}>
                                {formatNlDate((c.at || "").slice(0, 10))} — {c.by_name || "onbekend"}: {c.van?.tarief_ex_btw != null ? formatEuro(c.van.tarief_ex_btw) : "geen bedrag"} → {c.naar?.tarief_ex_btw != null ? formatEuro(c.naar.tarief_ex_btw) : "geen bedrag"} — "{c.reden}"
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                      {r.id && (
                        <Button variant="outline" size="sm" data-testid={`tarief-corrigeer-button-${r.id}`} onClick={() => openCorrigeren(r)}>
                          Corrigeren
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="mb-1 text-body font-semibold text-foreground">Reparaties → werkbonnen</h2>
        <p className="mb-3 text-data text-muted-foreground">
          Zet elke bestaande reparatie om in een werkbon (gereed, correctief). Handmatig ingevoerde
          bedragen blijven bewaard als "overgenomen uit reparatie"; verbruikte onderdelen worden één
          afgifte per reparatie, zonder de voorraad opnieuw te verlagen. Veilig om vaker te draaien.
        </p>
        <Button variant="secondary" data-testid="migratie-commit-button" onClick={migrate} disabled={migrating}>
          {migrating ? "Bezig..." : "Migratie uitvoeren"}
        </Button>
        {migratie && (
          <div className="mt-3 space-y-1 border-t border-lijn-zacht pt-3 text-body text-foreground" data-testid="migratie-rapport">
            <div>{migratie.counts.reparaties} reparaties gevonden · {migratie.counts.gemigreerd} gemigreerd · {migratie.counts.overgeslagen} stonden er al</div>
            <div className="text-data text-muted-foreground">{migratie.counts.afgiften} afgiften aangemaakt · {migratie.counts.storingen_gekoppeld} storingen gekoppeld</div>
            {(migratie.niet_geplaatst || []).length > 0 && (
              <ul className="mt-1 list-inside list-disc text-data text-letop-tekst">
                {migratie.niet_geplaatst.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>
        )}
      </Card>

      {/* §6b.2 Corrigeren dialog */}
      <Dialog open={!!corDlg} onOpenChange={(v) => !v && setCorDlg(null)}>
        <DialogContent srDescription="Corrigeer een verkeerd ingevoerd tariefbedrag. De ingangsdatum blijft staan; de oude waarde wordt in de historie vastgelegd.">
          <DialogHeader><DialogTitle>Tarief corrigeren</DialogTitle></DialogHeader>
          <p className="text-body text-muted-foreground">
            Gebruik dit alleen om een verkeerd ingevoerd bedrag te herstellen. De ingangsdatum ({corDlg?.ingangsdatum ? formatNlDate(corDlg.ingangsdatum) : ""}) blijft staan.
            Voor een tarief dat écht verandert, kies "Nieuw tarief per datum".
          </p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Tarief (mag leeg)</Label>
              <MoneyInput testid="tarief-corrigeer-bedrag" value={corForm.tarief} onChange={(v) => setCorForm((f) => ({ ...f, tarief: v }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Onderbouwing</Label>
              <Textarea data-testid="tarief-corrigeer-onderbouwing" rows={2} value={corForm.onderbouwing} onChange={(e) => setCorForm((f) => ({ ...f, onderbouwing: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Reden voor correctie <span className="text-fout-tekst">*</span></Label>
              <Textarea data-testid="tarief-corrigeer-reden" rows={2} value={corForm.reden} onChange={(e) => setCorForm((f) => ({ ...f, reden: e.target.value }))} placeholder="bijv. typo, bedrag stond een tientje te hoog" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCorDlg(null)}>Terug</Button>
            <Button data-testid="tarief-corrigeer-save" onClick={saveCorrigeren} disabled={corSaving || !corForm.reden.trim()}>
              {corSaving ? "Bezig..." : "Corrigeren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
